<template>
  <div class="dashboard-root">
    <div class="container-fluid">
      <!-- Breadcrumb -->
      <ol class="breadcrumb">
        <li class="active"><router-link to="/admin">Dashboard</router-link></li>
        <li class="active">Analytics</li>
      </ol>

      <!-- ── Date Filter Bar ─────────────────────────────────────────── -->
      <div class="filter-bar card-shadow">
        <div class="filter-presets">
          <button
            v-for="p in presets"
            :key="p.value"
            :class="['preset-btn', { active: selectedPreset === p.value }]"
            @click="selectPreset(p.value)"
          >{{ p.label }}</button>
        </div>
        <div class="filter-custom" v-if="selectedPreset === 'custom'">
          <input type="date" v-model="customFrom" class="date-input" placeholder="From" />
          <span class="date-sep">→</span>
          <input type="date" v-model="customTo" class="date-input" placeholder="To" />
          <button class="apply-btn" @click="loadAll">Apply</button>
        </div>
      </div>

      <!-- ── Summary Cards ───────────────────────────────────────────── -->
      <div class="summary-grid" v-if="!summaryLoading">
        <div class="kpi-card kpi-generated">
          <div class="kpi-icon"><i class="material-icons">confirmation_number</i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ summary.total_generated | numFormat }}</div>
            <div class="kpi-label">Coupons Generated</div>
          </div>
        </div>
        <div class="kpi-card kpi-printed">
          <div class="kpi-icon"><i class="material-icons">print</i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ summary.total_printed | numFormat }}</div>
            <div class="kpi-label">Coupons Printed</div>
          </div>
        </div>
        <div class="kpi-card kpi-scans">
          <div class="kpi-icon"><i class="material-icons">qr_code_scanner</i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ summary.total_scans | numFormat }}</div>
            <div class="kpi-label">Total Scans</div>
          </div>
        </div>
        <div class="kpi-card kpi-scan-pts">
          <div class="kpi-icon"><i class="material-icons">stars</i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ summary.total_scan_points | numFormat }}</div>
            <div class="kpi-label">Total Scan Points</div>
          </div>
        </div>
        <div class="kpi-card kpi-requests">
          <div class="kpi-icon"><i class="material-icons">assignment_turned_in</i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ summary.total_redemption_requests | numFormat }}</div>
            <div class="kpi-label">Redemption Requests</div>
          </div>
        </div>
        <div class="kpi-card kpi-redeem-pts">
          <div class="kpi-icon"><i class="material-icons">redeem</i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ summary.total_redemption_points | numFormat }}</div>
            <div class="kpi-label">Total Redemption Points</div>
          </div>
        </div>
        <div class="kpi-card kpi-redeemed">
          <div class="kpi-icon"><i class="material-icons">account_balance_wallet</i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ summary.total_redeemed_amount | numFormat }}</div>
            <div class="kpi-label">Total Redeemed Amount</div>
          </div>
        </div>
        <div class="kpi-card kpi-rate">
          <div class="kpi-icon"><i class="material-icons">trending_up</i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ summary.redemption_rate }}%</div>
            <div class="kpi-label">Redemption Rate</div>
          </div>
        </div>
      </div>
      <div v-else class="loading-cards">
        <div class="skeleton-card" v-for="i in 8" :key="i"></div>
      </div>

      <!-- ── Charts Row 1 ────────────────────────────────────────────── -->
      <div class="charts-row">
        <!-- Coupons Printed Over Time -->
        <div class="chart-card wide card-shadow">
          <div class="chart-header">
            <h4 class="chart-title"><i class="material-icons">bar_chart</i> Coupons Printed Over Time</h4>
            <button class="csv-btn" @click="downloadCsv('printedOverTime')">
              <i class="material-icons">download</i> CSV
            </button>
          </div>
          <div class="chart-body">
            <canvas id="chartPrinted"></canvas>
          </div>
        </div>

        <!-- Scan vs Redemption Trend -->
        <div class="chart-card wide card-shadow">
          <div class="chart-header">
            <h4 class="chart-title"><i class="material-icons">show_chart</i> Scan vs Redemption Trend</h4>
            <button class="csv-btn" @click="downloadCsv('scanVsRedemption')">
              <i class="material-icons">download</i> CSV
            </button>
          </div>
          <div class="chart-body">
            <canvas id="chartScanRedeem"></canvas>
          </div>
        </div>
      </div>

      <!-- Scan-to-Redemption Rate by Month -->
      <div class="charts-row">
        <div class="chart-card full card-shadow">
          <div class="chart-header">
            <h4 class="chart-title"><i class="material-icons">percent</i> {{ rateChartTitle }}</h4>
            <button class="csv-btn" @click="downloadCsv('rateByMonth')">
              <i class="material-icons">download</i> CSV
            </button>
          </div>
          <div class="chart-body">
            <canvas id="chartRate"></canvas>
          </div>
        </div>
      </div>

      <!-- ── Charts Row 2 ────────────────────────────────────────────── -->
      <div class="charts-row">
        <!-- Top Products -->
        <div class="chart-card half card-shadow">
          <div class="chart-header">
            <h4 class="chart-title"><i class="material-icons">inventory_2</i> Top 10 Products by Scans</h4>
            <button class="csv-btn" @click="downloadCsv('topProducts')">
              <i class="material-icons">download</i> CSV
            </button>
          </div>
          <div class="chart-body chart-body-tall">
            <canvas id="chartProducts"></canvas>
          </div>
        </div>

        <!-- Top Regions -->
        <div class="chart-card half card-shadow">
          <div class="chart-header">
            <h4 class="chart-title"><i class="material-icons">location_on</i> Top 10 Regions by Scans</h4>
            <button class="csv-btn" @click="downloadCsv('topRegions')">
              <i class="material-icons">download</i> CSV
            </button>
          </div>
          <div class="chart-body chart-body-tall">
            <canvas id="chartRegions"></canvas>
          </div>
        </div>
      </div>

    </div><!-- /container-fluid -->
  </div><!-- /dashboard-root -->
</template>

<script>
import moment from 'moment';
import commonServices from '../services/common-services';

export default {
  name: 'brand-admin',
  data() {
    return {
      // Filter state
      selectedPreset: 'this_month',
      customFrom: '',
      customTo: '',
      presets: [
        { label: 'Today',      value: 'today' },
        { label: 'Yesterday',  value: 'yesterday' },
        { label: 'This Week',  value: 'this_week' },
        { label: 'Last Week',  value: 'last_week' },
        { label: 'This Month', value: 'this_month' },
        { label: 'Last Month', value: 'last_month' },
        { label: 'This Year',  value: 'this_year' },
        { label: 'Last Year',  value: 'last_year' },
        { label: 'All Time',   value: 'all_time' },
        { label: 'Custom',     value: 'custom' },
      ],
      // Data
      summary: {
        total_printed: 0,
        total_scans: 0,
        total_scan_points: 0,
        total_redemption_points: 0,
        total_redemption_requests: 0,
        total_redeemed_amount: 0,
        redemption_rate: 0,
        total_generated: 0,
      },
      summaryLoading: true,
      // Chart data cache for CSV export
      chartData: {
        printedOverTime:  { labels: [], data: [] },
        scanVsRedemption: { labels: [], scan_points: [], redemption_points: [] },
        rateByMonth:      { labels: [], rate_by_month: [] },
        topProducts:      { labels: [], scan_counts: [], total_points: [] },
        topRegions:       { labels: [], scan_counts: [], total_points: [] },
      },
      // Chart instances
      charts: {},
      chartjsLoaded: false,
    };
  },
  computed: {
    periodUnit() {
      if (['today', 'yesterday'].includes(this.selectedPreset)) return 'Hour';
      if (['this_week', 'last_week', 'this_month', 'last_month'].includes(this.selectedPreset)) return 'Date';
      return 'Month';
    },
    rateChartTitle() {
      if (['today', 'yesterday'].includes(this.selectedPreset)) return 'Scan-to-Redemption Rate by Hour';
      if (['this_week', 'last_week', 'this_month', 'last_month'].includes(this.selectedPreset)) return 'Scan-to-Redemption Rate by Day';
      return 'Scan-to-Redemption Rate by Month';
    },
  },
  beforeCreate() {
    this.$store.commit('SET_LAYOUT', 'app-layout');
  },
  mounted() {
    this._loadSweetAlert();
    this._loadChartJS().then(() => {
      this.loadAll();
    });
  },
  methods: {
    // ── SweetAlert2 lazy loader ───────────────────────────────────────
    _loadSweetAlert() {
      return new Promise((resolve) => {
        if (window.Swal) {
          return resolve(window.Swal);
        }
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/sweetalert2@11';
        s.onload = () => resolve(window.Swal);
        s.onerror = () => resolve(null);
        document.head.appendChild(s);
      });
    },

    // ── Chart.js lazy loader ──────────────────────────────────────────
    _loadChartJS() {
      return new Promise((resolve) => {
        if (window.Chart) {
          this.chartjsLoaded = true;
          return resolve();
        }
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/chart.js@3.9.1/dist/chart.min.js';
        s.onload = () => { this.chartjsLoaded = true; resolve(); };
        document.head.appendChild(s);
      });
    },

    // ── Date filter ───────────────────────────────────────────────────
    selectPreset(val) {
      this.selectedPreset = val;
      if (val !== 'custom') this.loadAll();
    },

    _buildParams() {
      const p = { date_preset: this.selectedPreset };
      if (this.selectedPreset === 'custom') {
        p.date_from = this.customFrom;
        p.date_to   = this.customTo;
      }
      return p;
    },

    // ── Master loader ─────────────────────────────────────────────────
    loadAll() {
      this.loadSummary();
      this.loadPrintedOverTime();
      this.loadScanVsRedemption();
      this.loadTopProducts();
      this.loadTopRegions();
    },

    // ── API calls ─────────────────────────────────────────────────────
    loadSummary() {
      this.summaryLoading = true;
      commonServices.get('kspgAPI/DashboardReport/summary', this._buildParams())
        .then(r => {
          this.summary = r.data;
          this.summaryLoading = false;
        })
        .catch(() => { this.summaryLoading = false; });
    },

    loadPrintedOverTime() {
      commonServices.get('kspgAPI/DashboardReport/printedOverTime', this._buildParams())
        .then(r => {
          const d = r.data;
          this.chartData.printedOverTime = { labels: d.labels, data: d.data };
          this._renderPrinted(d.labels, d.data);
        });
    },

    loadScanVsRedemption() {
      commonServices.get('kspgAPI/DashboardReport/scanVsRedemption', this._buildParams())
        .then(r => {
          const d = r.data;
          this.chartData.scanVsRedemption = {
            labels: d.labels,
            scan_points: d.scan_points,
            redemption_points: d.redemption_points,
          };
          this.chartData.rateByMonth = { labels: d.labels, rate_by_month: d.rate_by_month };
          this._renderScanRedeem(d.labels, d.scan_points, d.redemption_points);
          this._renderRate(d.labels, d.rate_by_month);
        });
    },

    loadTopProducts() {
      commonServices.get('kspgAPI/DashboardReport/topProducts', this._buildParams())
        .then(r => {
          const d = r.data;
          this.chartData.topProducts = { labels: d.labels, scan_counts: d.scan_counts, total_points: d.total_points };
          this._renderTopProducts(d.labels, d.scan_counts);
        });
    },

    loadTopRegions() {
      commonServices.get('kspgAPI/DashboardReport/topRegions', this._buildParams())
        .then(r => {
          const d = r.data;
          this.chartData.topRegions = { labels: d.labels, scan_counts: d.scan_counts, total_points: d.total_points };
          this._renderTopRegions(d.labels, d.scan_counts);
        });
    },

    // ── Chart renderers ───────────────────────────────────────────────
    _destroyChart(id) {
      if (this.charts[id]) { this.charts[id].destroy(); delete this.charts[id]; }
    },

    _renderPrinted(labels, data) {
      this._destroyChart('printed');
      const ctx = document.getElementById('chartPrinted');
      if (!ctx || !window.Chart) return;
      this.charts['printed'] = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Coupons Printed',
            data,
            backgroundColor: 'rgba(99,102,241,0.75)',
            borderColor: 'rgba(99,102,241,1)',
            borderWidth: 1,
            borderRadius: 6,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
        }
      });
    },

    _renderScanRedeem(labels, scanPts, redeemPts) {
      this._destroyChart('scanRedeem');
      const ctx = document.getElementById('chartScanRedeem');
      if (!ctx || !window.Chart) return;
      this.charts['scanRedeem'] = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Scan Points',
              data: scanPts,
              borderColor: 'rgba(16,185,129,1)',
              backgroundColor: 'rgba(16,185,129,0.12)',
              tension: 0.4,
              fill: true,
              pointRadius: 4,
            },
            {
              label: 'Redemption Points',
              data: redeemPts,
              borderColor: 'rgba(239,68,68,1)',
              backgroundColor: 'rgba(239,68,68,0.10)',
              tension: 0.4,
              fill: true,
              pointRadius: 4,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'top' } },
          scales: { y: { beginAtZero: true, suggestedMin: 0, ticks: { precision: 0 } } }
        }
      });
    },

    _renderRate(labels, rates) {
      this._destroyChart('rate');
      const ctx = document.getElementById('chartRate');
      if (!ctx || !window.Chart) return;
      this.charts['rate'] = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Redemption Rate (%)',
            data: rates,
            borderColor: 'rgba(245,158,11,1)',
            backgroundColor: 'rgba(245,158,11,0.12)',
            tension: 0.4,
            fill: true,
            pointRadius: 4,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true, suggestedMin: 0, ticks: { callback: v => v + '%' } } }
        }
      });
    },

    _renderTopProducts(labels, counts) {
      this._destroyChart('products');
      const ctx = document.getElementById('chartProducts');
      if (!ctx || !window.Chart) return;
      const colors = labels.map((_, i) => `hsl(${(i * 36) % 360},70%,55%)`);
      this.charts['products'] = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Scans',
            data: counts,
            backgroundColor: colors,
            borderRadius: 4,
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { x: { beginAtZero: true, ticks: { precision: 0 } } }
        }
      });
    },

    _renderTopRegions(labels, counts) {
      this._destroyChart('regions');
      const ctx = document.getElementById('chartRegions');
      if (!ctx || !window.Chart) return;
      const colors = labels.map((_, i) => `hsl(${(i * 36 + 180) % 360},65%,55%)`);
      this.charts['regions'] = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Scans',
            data: counts,
            backgroundColor: colors,
            borderRadius: 4,
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { x: { beginAtZero: true, ticks: { precision: 0 } } }
        }
      });
    },

    // ── CSV Export ────────────────────────────────────────────────────
    async downloadCsv(type) {
      const fileNames = {
        printedOverTime:  'Coupons Printed Over Time',
        scanVsRedemption: 'Scan vs Redemption Trend',
        rateByMonth:      'Scan-to-Redemption Rate by Month',
        topProducts:      'Top 10 Products by Scans',
        topRegions:       'Top 10 Regions by Scans',
      };
      const fileName = fileNames[type] || type;
      const preset   = this.selectedPreset;
      const dateSuffix = moment().format('YYYYMMDD');

      const Swal = await this._loadSweetAlert();

      // Use loaded chart data for instant CSV download
      let rows = [];
      const d = this.chartData;
      switch (type) {
        case 'printedOverTime':
          rows.push([this.periodUnit, 'Coupons Printed']);
          (d.printedOverTime.labels || []).forEach((l, i) => rows.push([l, (d.printedOverTime.data || [])[i]]));
          break;
        case 'scanVsRedemption':
          rows.push([this.periodUnit, 'Scan Points', 'Redemption Points']);
          (d.scanVsRedemption.labels || []).forEach((l, i) => rows.push([l, (d.scanVsRedemption.scan_points || [])[i], (d.scanVsRedemption.redemption_points || [])[i]]));
          break;
        case 'rateByMonth':
          rows.push([this.periodUnit, 'Redemption Rate (%)']);
          (d.rateByMonth.labels || []).forEach((l, i) => rows.push([l, (d.rateByMonth.rate_by_month || [])[i]]));
          break;
        case 'topProducts':
          rows.push(['Product Name', 'Scan Count', 'Total Points']);
          (d.topProducts.labels || []).forEach((l, i) => rows.push([l, (d.topProducts.scan_counts || [])[i], (d.topProducts.total_points || [])[i]]));
          break;
        case 'topRegions':
          rows.push(['State / Region', 'Scan Count', 'Total Points']);
          (d.topRegions.labels || []).forEach((l, i) => rows.push([l, (d.topRegions.scan_counts || [])[i], (d.topRegions.total_points || [])[i]]));
          break;
      }

      if (rows.length <= 1) {
        if (Swal) {
          Swal.fire({
            icon: 'info',
            title: 'No Data Available',
            text: 'No data to export for this selected period.',
            timer: 2000,
            showConfirmButton: false,
          });
        }
        return;
      }

      this._triggerCsvDownload(rows, `${fileName}_${preset}_${dateSuffix}.csv`);
      if (Swal) {
        Swal.fire({
          icon: 'success',
          title: 'Downloaded!',
          text: 'Your CSV file has been downloaded successfully.',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    },

    _triggerCsvDownload(rows, filename) {
      const csv  = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.setAttribute('download', filename);
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
  filters: {
    numFormat(v) {
      if (v === null || v === undefined) return '0';
      return Number(v).toLocaleString('en-IN');
    },
  },
};
</script>

<style scoped>
/* ── Root ──────────────────────────────────────────────────────────────── */
.dashboard-root {
  font-family: 'Segoe UI', system-ui, sans-serif;
  background: #f4f6fb;
  min-height: 100vh;
  padding-bottom: 40px;
}

/* ── Card shadow util ───────────────────────────────────────────────────── */
.card-shadow {
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 2px 14px rgba(0,0,0,0.07);
}

/* ── Filter Bar ─────────────────────────────────────────────────────────── */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  margin-bottom: 24px;
}
.filter-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.preset-btn {
  padding: 6px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 20px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}
.preset-btn:hover {
  border-color: #6366f1;
  color: #6366f1;
}
.preset-btn.active {
  background: #6366f1;
  border-color: #6366f1;
  color: #fff;
  font-weight: 600;
}
.filter-custom {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
}
.date-input {
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
}
.date-sep { color: #6b7280; font-size: 16px; }
.apply-btn {
  padding: 6px 18px;
  background: #6366f1;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.2s;
}
.apply-btn:hover { background: #4f46e5; }

/* ── Summary Cards Grid ─────────────────────────────────────────────────── */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  margin-bottom: 28px;
}
@media (max-width: 1200px) {
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
.kpi-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 22px;
  border-radius: 14px;
  box-shadow: 0 2px 14px rgba(0,0,0,0.07);
  transition: transform 0.18s, box-shadow 0.18s;
}
.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 24px rgba(0,0,0,0.12);
}
.kpi-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi-icon .material-icons { font-size: 26px; color: #fff; }
.kpi-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  color: #111827;
  letter-spacing: -0.5px;
}
.kpi-label {
  font-size: 12px;
  color: #6b7280;
  margin-top: 5px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
/* Card colour themes */
.kpi-generated{ border-left: 4px solid #14b8a6; } .kpi-generated .kpi-icon { background: linear-gradient(135deg, #14b8a6, #2dd4bf); }
.kpi-printed  { border-left: 4px solid #6366f1; } .kpi-printed  .kpi-icon { background: linear-gradient(135deg, #6366f1, #818cf8); }
.kpi-scans    { border-left: 4px solid #10b981; } .kpi-scans    .kpi-icon { background: linear-gradient(135deg, #10b981, #34d399); }
.kpi-scan-pts { border-left: 4px solid #f59e0b; } .kpi-scan-pts .kpi-icon { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.kpi-requests { border-left: 4px solid #3b82f6; } .kpi-requests .kpi-icon { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.kpi-redeem-pts { border-left: 4px solid #ef4444; } .kpi-redeem-pts .kpi-icon { background: linear-gradient(135deg, #ef4444, #f87171); }
.kpi-redeemed { border-left: 4px solid #8b5cf6; } .kpi-redeemed .kpi-icon { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.kpi-rate     { border-left: 4px solid #06b6d4; } .kpi-rate     .kpi-icon { background: linear-gradient(135deg, #06b6d4, #22d3ee); }

/* Skeleton loading */
.loading-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  margin-bottom: 28px;
}
@media (max-width: 1200px) {
  .loading-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .loading-cards {
    grid-template-columns: 1fr;
  }
}
.skeleton-card {
  height: 92px;
  border-radius: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Chart Cards ────────────────────────────────────────────────────────── */
.charts-row {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.chart-card {
  padding: 20px 24px;
  border-radius: 14px;
  box-shadow: 0 2px 14px rgba(0,0,0,0.07);
  background: #fff;
  min-width: 0;
}
.chart-card.wide { flex: 1; min-width: 320px; }
.chart-card.full { flex: 1 1 100%; }
.chart-card.half { flex: 1; min-width: 280px; }

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.chart-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 7px;
}
.chart-title .material-icons { font-size: 18px; color: #6366f1; }
.chart-body { position: relative; height: 260px; }
.chart-body-tall { height: 340px; }

.csv-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: 1.5px solid #6366f1;
  border-radius: 8px;
  color: #6366f1;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.csv-btn .material-icons { font-size: 15px; }
.csv-btn:hover { background: #6366f1; color: #fff; }
</style>
